/**
 * Sport-aware player headshot URL resolver.
 *
 * - MLB: picks-core blob storage at mlb/headshots/{TEAM}/{first_last}.png
 * - NBA: CDN at cdn.nba.com using the Sportradar external player ID (which is the NBA.com player ID)
 * - NFL: No reliable public URL pattern by player ID — returns null; callers fall back to jersey/initials avatar.
 */

const STORAGE_ACCOUNT = import.meta.env.VITE_BLOB_STORAGE_ACCOUNT_NAME || 'draftkingsdev';
const CONTAINER = import.meta.env.VITE_BLOB_STORAGE_CONTAINER || 'picks-core';
const CDN_BASE_URL = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}`;

/** Feed abbreviations that differ from blob folder names (MLB only). */
const MLB_TEAM_ABBR_MAP: Record<string, string> = {
  AZ: 'ARI',
  ANA: 'LAA',
};

/**
 * MLB headshot: picks-core/mlb/headshots/{TEAM}/{first_last}.png
 * Matches admin `generateMLBHeadshotUrl` / jersey-mapping behavior.
 */
function generateMLBHeadshotUrl(teamAbbr: string, firstName: string, lastName: string): string {
  const folder = MLB_TEAM_ABBR_MAP[teamAbbr] || teamAbbr;
  const normalize = (s: string) => s.toLowerCase().replace(/\./g, '_').replace(/\s+/g, '_');
  return `${CDN_BASE_URL}/mlb/headshots/${folder}/${normalize(firstName)}_${normalize(lastName)}.png`;
}

/**
 * NBA headshot: cdn.nba.com/headshots/nba/latest/1040x760/{externalId}.png
 * Sportradar player IDs for NBA map directly to NBA.com player IDs.
 */
function generateNBAHeadshotUrl(externalId: string): string {
  return `https://cdn.nba.com/headshots/nba/latest/1040x760/${externalId}.png`;
}

/**
 * Resolve a player headshot URL for any supported sport.
 *
 * @param sport - Sport code (e.g. 'MLB', 'NBA', 'NFL')
 * @param externalId - Sportradar player external ID
 * @param teamAbbr - Team abbreviation (required for MLB)
 * @param firstName - Player first name (required for MLB)
 * @param lastName - Player last name (required for MLB)
 * @returns URL string, or null if no headshot resolver exists for this sport (e.g. NFL).
 */
export function resolvePlayerHeadshotUrl(
  sport: string | null | undefined,
  externalId: string | null | undefined,
  teamAbbr: string | null | undefined,
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string | null {
  if (!sport) return null;

  switch (sport.toUpperCase()) {
    case 'MLB':
      if (teamAbbr && firstName && lastName) {
        return generateMLBHeadshotUrl(teamAbbr, firstName, lastName);
      }
      return null;

    case 'NBA':
      if (externalId) {
        return generateNBAHeadshotUrl(externalId);
      }
      return null;

    // NFL: no reliable public headshot URL pattern per player ID.
    // Callers should fall back to the jersey/initials avatar.
    default:
      return null;
  }
}
